import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  Clock,
  User,
  Phone,
  Store,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  X,
  Play,
  Pause,
  Volume2,
  Filter
} from 'lucide-react';
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { format, subDays, subWeeks, subMonths, startOfWeek, startOfMonth } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mini Audio Player
const MiniAudioPlayer = ({ audioUrl }) => {
  const audioRef = React.useRef(null);
  const progressRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = async (e) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.error('Audio play error:', err);
        }
      }
    }
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    if (audioRef.current && progressRef.current && duration) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const pct = clickX / rect.width;
      audioRef.current.currentTime = pct * duration;
    }
  };

  if (!audioUrl) return <span className="text-xs text-slate-400">No audio</span>;

  return (
    <div className="flex items-center gap-2">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onTimeUpdate={() => setProgress((audioRef.current?.currentTime / audioRef.current?.duration) * 100 || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => setIsPlaying(false)}
      />
      <button
        onClick={togglePlay}
        className="w-7 h-7 rounded-full bg-[#2eb4ac] hover:bg-[#259790] text-white flex items-center justify-center"
      >
        {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
      </button>
      <div 
        ref={progressRef}
        onClick={handleSeek}
        className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden cursor-pointer"
      >
        <div className="h-full bg-[#2eb4ac]" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

// Date range presets
const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ from: format(new Date(), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'This Week', getValue: () => ({ from: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'This Month', getValue: () => ({ from: format(startOfMonth(new Date()), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'Last 7 Days', getValue: () => ({ from: format(subDays(new Date(), 7), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'Last 30 Days', getValue: () => ({ from: format(subDays(new Date(), 30), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'All Time', getValue: () => ({ from: '', to: '' }) },
];

// Grouped Sellers View
const GroupedSellersView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalGroups, setTotalGroups] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    fetchGroupedCalls();
  }, [currentPage, dateFrom, dateTo]);

  const fetchGroupedCalls = async () => {
    setLoading(true);
    try {
      let url = `${API}/calls/grouped?page=${currentPage}&limit=${pageSize}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const response = await axios.get(url);
      setGroups(response.data.groups || []);
      setTotalGroups(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching grouped calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset) => {
    const { from, to } = preset.getValue();
    setDateFrom(from);
    setDateTo(to);
    setCurrentPage(1);
    setShowDateFilter(false);
  };

  const filteredGroups = groups.filter(g => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      g.phone?.includes(q) ||
      g.seller_name?.toLowerCase().includes(q) ||
      g.store_name?.toLowerCase().includes(q) ||
      g.seller_id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="grouped-sellers-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Seller Call Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Calls grouped by phone number · {totalGroups} unique sellers</p>
        </div>
        <Link 
          to="/call-logs" 
          className="text-sm text-[#2eb4ac] hover:underline"
        >
          Switch to Individual Calls View →
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by phone, seller name, store..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm"
          />
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowDateFilter(!showDateFilter)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
              dateFrom || dateTo ? 'bg-[#2eb4ac]/10 border-[#2eb4ac] text-[#2eb4ac]' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Calendar className="w-4 h-4" />
            {dateFrom || dateTo ? `${dateFrom || 'Start'} - ${dateTo || 'End'}` : 'Date Range'}
            <ChevronDown className="w-4 h-4" />
          </button>
          
          {showDateFilter && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-50 p-3">
              <div className="space-y-2 mb-3">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handleDatePreset(preset)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="border-t pt-3 space-y-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="From"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="To"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone / Seller</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Calls</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Interested</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Not Interested</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Latest Call</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Latest Verdict</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2 text-slate-500">
                    <div className="w-5 h-5 border-2 border-[#2eb4ac] border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </div>
                </td>
              </tr>
            ) : filteredGroups.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                  No sellers found
                </td>
              </tr>
            ) : (
              filteredGroups.map((group) => (
                <tr 
                  key={group.phone}
                  onClick={() => navigate(`/seller-calls/${group.phone}`)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{group.phone || 'Unknown'}</p>
                        {group.seller_name && (
                          <p className="text-xs text-slate-500">{group.seller_name} {group.store_name && `· ${group.store_name}`}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {group.seller_status === 'existing' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                        <Store className="w-3 h-3" />
                        Existing · {group.seller_id}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                        <User className="w-3 h-3" />
                        New Seller
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-lg font-semibold text-slate-900">{group.total_calls}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                      <ThumbsUp className="w-3.5 h-3.5" />
                      {group.interested_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-rose-600 font-medium">
                      <ThumbsDown className="w-3.5 h-3.5" />
                      {group.not_interested_count}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {group.latest_call_date && (
                      <span className="text-sm text-slate-600">
                        {format(new Date(group.latest_call_date), 'MMM d, yyyy')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {group.latest_verdict === 'Interested' ? (
                      <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                        Interested
                      </span>
                    ) : group.latest_verdict === 'Not Interested' ? (
                      <span className="px-2.5 py-1 bg-rose-50 text-rose-700 text-xs font-medium rounded-full">
                        Not Interested
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalGroups)} of {totalGroups}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Seller Call Timeline (Drill-down)
const SellerCallTimeline = () => {
  const { phone } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('desc'); // newest first by default
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    fetchSellerCalls();
  }, [phone, sortOrder, dateFrom, dateTo]);

  const fetchSellerCalls = async () => {
    setLoading(true);
    try {
      let url = `${API}/calls/by-phone/${phone}?sort_order=${sortOrder}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching seller calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset) => {
    const { from, to } = preset.getValue();
    setDateFrom(from);
    setDateTo(to);
    setShowDateFilter(false);
  };

  const getSentimentColor = (verdict, stage) => {
    if (verdict === 'Interested') {
      if (stage === 'Hot') return 'bg-emerald-500';
      if (stage === 'Warm') return 'bg-emerald-400';
      return 'bg-emerald-300';
    }
    return 'bg-rose-400';
  };

  const getSentimentBadge = (verdict, stage) => {
    if (verdict === 'Interested') {
      return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          stage === 'Hot' ? 'bg-emerald-100 text-emerald-700' :
          stage === 'Warm' ? 'bg-yellow-100 text-yellow-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {stage || 'Interested'}
        </span>
      );
    }
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">Not Interested</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-3 border-[#2eb4ac] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seller-call-timeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/seller-calls')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{phone}</h1>
              {data?.seller_status === 'existing' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full">
                  <Store className="w-4 h-4" />
                  Existing Seller · {data?.seller_info?.seller_id}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 text-sm font-medium rounded-full">
                  <User className="w-4 h-4" />
                  New Seller
                </span>
              )}
            </div>
            {data?.seller_info && (
              <p className="text-sm text-slate-500 mt-1">
                {data.seller_info.name} {data.seller_info.store_name && `· ${data.seller_info.store_name}`}
              </p>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-500">{data?.total_calls || 0} calls</p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"
        >
          {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
          {sortOrder === 'asc' ? 'Oldest First' : 'Newest First'}
        </button>
        
        <div className="relative">
          <button
            onClick={() => setShowDateFilter(!showDateFilter)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-medium transition-colors ${
              dateFrom || dateTo ? 'bg-[#2eb4ac]/10 border-[#2eb4ac] text-[#2eb4ac]' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            {dateFrom || dateTo ? `${dateFrom || 'Start'} - ${dateTo || 'End'}` : 'Filter by Date'}
          </button>
          
          {showDateFilter && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-50 p-3">
              <div className="space-y-2 mb-3">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handleDatePreset(preset)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 rounded-lg"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="border-t pt-3 space-y-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Call Timeline */}
      <div className="space-y-4">
        {data?.calls?.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No calls found for this phone number</p>
          </div>
        ) : (
          data?.calls?.map((call, idx) => (
            <div
              key={call.id}
              className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-slate-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left - Timeline indicator */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${getSentimentColor(call.final_verdict, call.lifecycle_stage)}`} />
                    {idx < (data?.calls?.length || 0) - 1 && (
                      <div className="w-0.5 h-full bg-slate-200 mt-1" style={{ minHeight: '80px' }} />
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {/* Date & Time */}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {call.analyzed_at ? format(new Date(call.analyzed_at), 'MMM d, yyyy') : '-'}
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-500">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {call.analyzed_at ? format(new Date(call.analyzed_at), 'h:mm a') : '-'}
                      </span>
                      {call.duration && (
                        <span className="text-slate-500">{Math.floor(call.duration / 60)}m {call.duration % 60}s</span>
                      )}
                    </div>
                    
                    {/* Sentiment & Outcome */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {getSentimentBadge(call.final_verdict, call.lifecycle_stage)}
                      
                      {call.classification && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                          {call.classification}
                        </span>
                      )}
                      
                      {(call.interested_analysis?.primary_driver || call.not_interested_analysis?.primary_reason) && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">
                          {call.interested_analysis?.primary_driver || call.not_interested_analysis?.primary_reason}
                        </span>
                      )}
                    </div>
                    
                    {/* Summary */}
                    {call.summary && (
                      <p className="text-sm text-slate-600 line-clamp-2">{call.summary}</p>
                    )}
                    
                    {/* Next Action */}
                    {call.next_action && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-500">Next Action:</span>
                        <span className="text-xs text-slate-700">{call.next_action}</span>
                      </div>
                    )}
                    
                    {/* Audio Player */}
                    {call.audio_url && (
                      <MiniAudioPlayer audioUrl={`/api/audio/${encodeURIComponent(call.audio_url.split('/').pop())}`} />
                    )}
                  </div>
                </div>
                
                {/* Right - Score & Actions */}
                <div className="flex flex-col items-end gap-2">
                  {call.quality_score && (
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        call.quality_score >= 70 ? 'text-emerald-600' :
                        call.quality_score >= 40 ? 'text-amber-600' : 'text-rose-600'
                      }`}>
                        {call.quality_score}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase">Score</div>
                    </div>
                  )}
                  
                  <Link
                    to={`/calls/${call.id}`}
                    className="text-xs text-[#2eb4ac] hover:underline"
                  >
                    View Details →
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export { GroupedSellersView, SellerCallTimeline };
export default GroupedSellersView;
